import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCurrentLocale } from "../lib/useLocaleNav.js";

export function Reveal({ children, delay = 0, as: Tag = "div", className = "", style = {} }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setShown(true); io.disconnect(); } },
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <Tag ref={ref} className={`reveal ${shown ? "in" : ""} ${className}`} style={{ ...style, transitionDelay: `${delay}ms` }}>
      {children}
    </Tag>
  );
}

export function Wordmark({ size = 22, variant = "default" }) {
  const isReverse = variant === "reverse";
  const filter = isReverse ? "brightness(0) invert(1)" : "none";
  const h = size;
  const w = Math.round(h * (400.8 / 79));
  const locale = useCurrentLocale();
  const { t } = useTranslation();
  return (
    <Link to={`/${locale}`} aria-label={t("ui.wordmarkAlt")} style={{ display: "inline-flex", alignItems: "center", textDecoration: "none", lineHeight: 0 }}>
      <img src="/assets/TableTalk_logo.svg" alt="TableTalk" style={{ height: h, width: w, display: "block", filter }} />
    </Link>
  );
}

export function Eyebrow({ children, color }) {
  return (
    <div className="tag" style={color ? { color } : undefined}>
      <span>{children}</span>
    </div>
  );
}

export function BigNum({ value, label, sub, color = "var(--persimmon)", size = "clamp(80px, 11vw, 168px)" }) {
  return (
    <div>
      <div className="tt-big" style={{ fontSize: size, color }}>{value}</div>
      <div style={{ marginTop: 18, fontFamily: "Satoshi, sans-serif", fontWeight: 600, fontSize: 17, color: "var(--ink)" }}>{label}</div>
      {sub && <div style={{ color: "var(--muted)", fontSize: 14.5, marginTop: 6, lineHeight: 1.5, maxWidth: 280 }}>{sub}</div>}
    </div>
  );
}

export function FaqItem({ q, a, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ borderBottom: "1px solid var(--line)" }}>
      <button onClick={() => setOpen(o => !o)} aria-expanded={open}
        style={{ all: "unset", cursor: "pointer", display: "flex", width: "100%", alignItems: "center", justifyContent: "space-between", padding: "24px 4px", gap: 24 }}>
        <span style={{ fontFamily: "Satoshi, sans-serif", fontSize: "clamp(18px, 1.4vw, 21px)", fontWeight: 600, letterSpacing: "-.01em", color: "var(--ink)" }}>{q}</span>
        <span style={{
          width: 34, height: 34, borderRadius: 999, border: "1px solid var(--line-2)",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          color: open ? "var(--bg)" : "var(--ink)",
          background: open ? "var(--ink)" : "transparent",
          flexShrink: 0,
          transform: open ? "rotate(45deg)" : "rotate(0)",
          transition: "transform .3s ease, background .2s ease, color .2s ease"
        }}>
          <Icon name="plus" size={15} stroke={1.6} />
        </span>
      </button>
      <div style={{ display: "grid", gridTemplateRows: open ? "1fr" : "0fr", transition: "grid-template-rows .35s ease" }}>
        <div style={{ overflow: "hidden" }}>
          <div style={{ padding: "0 4px 26px", maxWidth: 720, color: "var(--ink-2)", fontSize: "17px", lineHeight: 1.65 }}>{a}</div>
        </div>
      </div>
    </div>
  );
}

export function FairCommitment({ compact = false }) {
  const { t } = useTranslation();
  return (
    <section style={{
      background: compact ? "transparent" : "var(--bg-2)",
      padding: compact ? 0 : "120px 0",
      borderTop: compact ? 0 : "1px solid var(--line)",
      borderBottom: compact ? 0 : "1px solid var(--line)"
    }}>
      <div className="container">
        <div style={{
          display: "grid",
          gridTemplateColumns: compact ? "1fr" : "minmax(0, 0.85fr) minmax(0, 1.15fr)",
          gap: 64, alignItems: "flex-start"
        }} className="fair-grid">
          <div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 12, fontFamily: "Inter, sans-serif", fontSize: 12, fontWeight: 500, letterSpacing: ".16em", textTransform: "uppercase", color: "var(--ink-2)" }}>
              <span style={{ width: 22, height: 1, background: "var(--marigold)" }} />
              {t("ui.fairCommitment.eyebrow")}
            </div>
          </div>
          <div>
            <div style={{ width: 48, height: 1, background: "var(--marigold)", marginBottom: 28 }} />
            <p className="tt-edit" style={{ margin: 0, fontSize: "clamp(28px, 3.4vw, 44px)", lineHeight: 1.22, color: "var(--ink)" }}>
              {t("ui.fairCommitment.headline")}
            </p>
            <p style={{ marginTop: 28, color: "var(--ink-2)", fontSize: 18, lineHeight: 1.65, maxWidth: 640 }}>
              {t("ui.fairCommitment.body")}
            </p>
            <div style={{ marginTop: 24, fontSize: 13, color: "var(--muted)", letterSpacing: ".04em" }}>{t("ui.fairCommitment.signature")}</div>
          </div>
        </div>
      </div>
      <style>{`
        @media (max-width: 900px) {
          .fair-grid { grid-template-columns: 1fr !important; gap: 32px !important; }
        }
      `}</style>
    </section>
  );
}

export function Editorial({ children, attribution, color = "var(--ink)" }) {
  return (
    <div style={{ padding: "8px 0" }}>
      <div style={{ width: 48, height: 1, background: "var(--persimmon)", marginBottom: 24 }} />
      <p className="tt-edit" style={{ margin: 0, fontSize: "clamp(22px, 2.6vw, 32px)", lineHeight: 1.3, color, maxWidth: 760 }}>{children}</p>
      {attribution && <div style={{ marginTop: 20, fontFamily: "Inter, sans-serif", fontSize: 13, color: "var(--muted)", letterSpacing: ".04em" }}>— {attribution}</div>}
    </div>
  );
}

export function NFCCard({ size = "lg", style = {} }) {
  const { t } = useTranslation();
  const sizes = {
    sm: { w: 240, h: 150, fs: 18, sub: 11 },
    md: { w: 320, h: 200, fs: 22, sub: 12 },
    lg: { w: 420, h: 264, fs: 28, sub: 13 }
  };
  const s = sizes[size] || sizes.lg;
  return (
    <div style={{
      width: s.w, height: s.h, borderRadius: 14,
      background: "linear-gradient(135deg, var(--persimmon) 0%, var(--marigold) 100%)",
      color: "var(--bg)", padding: "22px 26px",
      display: "flex", flexDirection: "column", justifyContent: "space-between",
      boxShadow: "0 30px 60px -28px rgba(232, 90, 26, 0.45), 0 8px 16px -8px rgba(16,14,12,0.15)",
      position: "relative", overflow: "hidden",
      ...style
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <img src="/assets/TableTalk_logo.svg" alt="TableTalk" style={{ height: s.fs, display: "block", filter: "brightness(0) invert(1)" }} />
        <NFCSymbol size={s.fs * 1.5} />
      </div>
      <div>
        <div style={{ fontSize: s.sub, letterSpacing: ".16em", textTransform: "uppercase", opacity: .78, fontWeight: 500 }}>{t("ui.nfcCard.tap")}</div>
        <div style={{ fontFamily: "Satoshi, sans-serif", fontWeight: 700, fontSize: s.fs * 1.1, lineHeight: 1.05, marginTop: 8, letterSpacing: "-0.02em", maxWidth: "80%" }}>{t("ui.nfcCard.headline")}</div>
      </div>
    </div>
  );
}

export function NFCSymbol({ size = 36 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
      <path d="M5 8c2-2 4-3 7-3s5 1 7 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" opacity=".4" />
      <path d="M7 11c1.5-1.5 3-2 5-2s3.5.5 5 2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" opacity=".7" />
      <path d="M9 14c1-1 2-1.5 3-1.5s2 .5 3 1.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="12" cy="17" r="1.4" fill="currentColor" />
    </svg>
  );
}

export function PhotoFrame({ ratio = "4/3", tone = "ink", caption, hint, label = "Photo", src, alt, style = {} }) {
  const dark = tone === "ink";
  const bg = dark
    ? "var(--ink)"
    : tone === "wash"
      ? "linear-gradient(135deg, var(--wash-1) 0%, var(--wash-2) 100%)"
      : "var(--bg-2)";
  const fg = dark ? "var(--bg)" : "var(--ink)";
  const tickColor = dark ? "rgba(255,255,255,.35)" : "var(--line-2)";
  if (src) {
    return (
      <figure style={{ margin: 0, ...style }}>
        <div style={{ position: "relative", overflow: "hidden", aspectRatio: ratio, borderRadius: 4, background: bg }}>
          <img src={src} alt={alt || caption || label} loading="lazy" decoding="async" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
        </div>
      </figure>
    );
  }
  return (
    <figure style={{ margin: 0, ...style }}>
      <div style={{
        position: "relative", overflow: "hidden",
        aspectRatio: ratio, background: bg, color: fg,
        padding: "22px 24px",
        display: "flex", flexDirection: "column", justifyContent: "space-between",
        borderRadius: 4
      }}>
        <CornerTicks color={tickColor} />
        {dark && (
          <div aria-hidden="true" style={{
            position: "absolute", inset: 0,
            backgroundImage: "repeating-linear-gradient(135deg, rgba(255,255,255,.03) 0 1px, transparent 1px 18px)",
            pointerEvents: "none"
          }} />
        )}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", position: "relative", zIndex: 1 }}>
          <span style={{ fontFamily: "Inter, sans-serif", fontSize: 11, letterSpacing: ".18em", textTransform: "uppercase", fontWeight: 600, opacity: dark ? .7 : .55 }}>{label}</span>
          <CameraGlyph color={dark ? "rgba(255,255,255,.5)" : "var(--muted)"} />
        </div>
        <div style={{ position: "relative", zIndex: 1, maxWidth: "92%" }}>
          {caption && (
            <div style={{ fontFamily: "Fraunces, serif", fontStyle: "italic", fontSize: "clamp(15px, 1.35vw, 19px)", lineHeight: 1.35, color: dark ? "rgba(255,255,255,.95)" : "var(--ink)" }}>{caption}</div>
          )}
          {hint && (
            <div style={{ marginTop: 8, fontSize: 11.5, letterSpacing: ".05em", opacity: dark ? .55 : .65 }}>{hint}</div>
          )}
        </div>
      </div>
    </figure>
  );
}

function CornerTicks({ color = "rgba(255,255,255,.35)" }) {
  const tick = (pos) => ({ position: "absolute", width: 14, height: 14, borderColor: color, ...pos });
  return (
    <>
      <span style={{ ...tick({ top: 10, left: 10 }), borderTop: "1px solid", borderLeft: "1px solid" }} />
      <span style={{ ...tick({ top: 10, right: 10 }), borderTop: "1px solid", borderRight: "1px solid" }} />
      <span style={{ ...tick({ bottom: 10, left: 10 }), borderBottom: "1px solid", borderLeft: "1px solid" }} />
      <span style={{ ...tick({ bottom: 10, right: 10 }), borderBottom: "1px solid", borderRight: "1px solid" }} />
    </>
  );
}

function CameraGlyph({ color = "currentColor", size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.4">
      <path d="M3 7.5h3.5l1.2-2h8.6l1.2 2H21v11.5H3z" strokeLinejoin="round" />
      <circle cx="12" cy="13" r="3.6" />
    </svg>
  );
}

export function VideoFrame({ ratio = "16/9", src, poster, caption, hint, label = "Video · 90 sec", duration = "01:30", style = {} }) {
  return (
    <figure style={{ margin: 0, ...style }}>
      <div style={{ position: "relative", overflow: "hidden", aspectRatio: ratio, background: "var(--ink)", color: "var(--bg)", borderRadius: 6 }}>
        {src ? (
          <video src={src} poster={poster} controls playsInline preload="metadata" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <>
            <div aria-hidden="true" style={{ position: "absolute", inset: 0, backgroundImage: "repeating-linear-gradient(135deg, rgba(255,255,255,.04) 0 1px, transparent 1px 14px)", pointerEvents: "none" }} />
            <div aria-hidden="true" style={{ position: "absolute", inset: 0, background: "radial-gradient(circle at 50% 45%, rgba(255,107,67,.08) 0%, transparent 55%)", pointerEvents: "none" }} />
            <CornerTicks color="rgba(255,255,255,.35)" />
            <div style={{ position: "absolute", top: 18, left: 24, right: 24, display: "flex", justifyContent: "space-between", alignItems: "center", zIndex: 2 }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8, fontFamily: "Inter, sans-serif", fontSize: 11, letterSpacing: ".22em", textTransform: "uppercase", color: "rgba(255,255,255,.78)", fontWeight: 600 }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--persimmon)", animation: "tt-rec-blink 1.6s ease-in-out infinite" }} />
                {label}
              </span>
              <span style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 12, letterSpacing: ".08em", color: "rgba(255,255,255,.62)" }}>00:00 / {duration}</span>
            </div>
            <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2 }}>
              <div style={{ width: "clamp(72px, 9vw, 112px)", aspectRatio: "1", borderRadius: "50%", border: "1px solid rgba(255,255,255,.4)", background: "rgba(0,0,0,.18)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--bg)" }}>
                <svg width="32%" height="32%" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8 5.5v13l11-6.5z" />
                </svg>
              </div>
            </div>
            <div style={{ position: "absolute", left: 24, right: 24, bottom: 20, zIndex: 2 }}>
              <div style={{ height: 2, background: "rgba(255,255,255,.16)", borderRadius: 99, overflow: "hidden" }}>
                <div style={{ height: "100%", width: "14%", background: "var(--persimmon)" }} />
              </div>
              {caption && (
                <div style={{ marginTop: 18, maxWidth: "90%", fontFamily: "Fraunces, serif", fontStyle: "italic", fontSize: "clamp(15px, 1.4vw, 20px)", lineHeight: 1.35, color: "rgba(255,255,255,.95)" }}>{caption}</div>
              )}
              {hint && (
                <div style={{ marginTop: 8, fontSize: 11.5, letterSpacing: ".05em", color: "rgba(255,255,255,.55)" }}>{hint}</div>
              )}
            </div>
          </>
        )}
      </div>
      <style>{`
        @keyframes tt-rec-blink { 0%, 100% { opacity: 1; } 50% { opacity: .35; } }
        @media (prefers-reduced-motion: reduce) { @keyframes tt-rec-blink { 0%,100% { opacity:1 } } }
      `}</style>
    </figure>
  );
}

// re-export for components that pull Icon through ui
import Icon from "./icons.jsx";
export { Icon };
